import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { TrendingDown, TrendingUp, Zap } from 'lucide-react';

export function ImpactHero() {
  const [chaos, setChaos] = useState(100);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setChaos(prev => Math.max(0, prev - 1));
    }, 30);
    
    return () => clearInterval(interval);
  }, []);

  // Partículas caóticas que se organizam
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    initialX: Math.random() * 100,
    initialY: Math.random() * 100,
    finalX: 50 + (Math.cos((i / 40) * Math.PI * 2) * 30),
    finalY: 50 + (Math.sin((i / 40) * Math.PI * 2) * 30),
  }));

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#0A0E1A] via-[#0B0E14] to-[#0A0E1A]">
      {/* Animated Background Particles */}
      <div className="absolute inset-0">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-2 h-2 rounded-full"
            style={{
              background: `linear-gradient(135deg, #FF6B35 0%, #4ECDC4 100%)`,
              boxShadow: '0 0 10px rgba(255, 107, 53, 0.5)',
            }}
            initial={{
              x: `${particle.initialX}vw`,
              y: `${particle.initialY}vh`,
              opacity: 0.3,
            }}
            animate={{
              x: `${particle.finalX}vw`,
              y: `${particle.finalY}vh`,
              opacity: chaos > 0 ? 0.3 : 0.8,
            }}
            transition={{
              duration: 3,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Grid Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(78, 205, 196, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(78, 205, 196, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }} />
      </div>

      <div className="relative z-10 container mx-auto px-6 text-center">
        {/* Chaos/Control Indicator */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-[#FF6B35]/30 bg-[#0B0E14]/80 backdrop-blur-lg mb-8"
        >
          {chaos > 50 ? (
            <>
              <TrendingDown className="w-5 h-5 text-[#FF6B35]" />
              <span className="text-[#FF6B35] font-mono text-sm">CAOS: {chaos}%</span>
            </>
          ) : (
            <>
              <TrendingUp className="w-5 h-5 text-[#4ECDC4]" />
              <span className="text-[#4ECDC4] font-mono text-sm">CONTROLE: {100 - chaos}%</span>
            </>
          )}
          <div className="w-32 h-1.5 bg-[#0B0E14] rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#FF6B35] to-[#4ECDC4]"
              initial={{ width: '100%' }}
              animate={{ width: `${100 - chaos}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-6xl md:text-7xl lg:text-8xl font-bold mb-6"
          style={{ fontFamily: 'Sora, sans-serif' }}
        >
          <span className="block text-white mb-4">Do Caos</span>
          <span className="block bg-gradient-to-r from-[#FF6B35] via-[#FFE66D] to-[#4ECDC4] bg-clip-text text-transparent">
            Ao Controle Total
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          Veja como <span className="text-[#FFE66D] font-semibold">1.247 empresas</span> transformaram 
          dados desorganizados em <span className="text-[#4ECDC4] font-semibold">decisões lucrativas</span> com WPTT
        </motion.p>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12"
        >
          {[
            { label: 'Economia Média', value: 'R$ 47.000', suffix: '/mês', color: '#10B981' },
            { label: 'Tempo Economizado', value: '156h', suffix: '/mês', color: '#4ECDC4' },
            { label: 'ROI Médio', value: '847%', suffix: 'em 90 dias', color: '#FFE66D' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 + index * 0.1 }}
              className="relative group"
            >
              <div className="p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm hover:border-white/20 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-bold mb-2" style={{ color: stat.color }}>
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 font-mono">{stat.suffix}</div>
                <div className="text-xs text-gray-500 mt-2">{stat.label}</div>
                
                {/* Glow Effect */}
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl -z-10"
                  style={{ background: `radial-gradient(circle at center, ${stat.color}40 0%, transparent 70%)` }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group relative px-8 py-4 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] rounded-xl font-semibold text-white text-lg shadow-2xl overflow-hidden"
          >
            <span className="relative z-10 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Ver Minha Transformação
            </span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-[#FF8C42] to-[#FF6B35]"
              initial={{ x: '-100%' }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            className="px-8 py-4 border-2 border-[#4ECDC4]/50 rounded-xl font-semibold text-[#4ECDC4] text-lg hover:bg-[#4ECDC4]/10 transition-all"
          >
            Ver Casos de Sucesso
          </motion.button>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, repeat: Infinity, duration: 2, repeatType: 'reverse' }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-[#4ECDC4]/50 rounded-full p-1">
            <motion.div
              className="w-1.5 h-1.5 bg-[#4ECDC4] rounded-full mx-auto"
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
